import { Button } from '@heroui/react';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { labelForWeek } from '@/patient-app-common/helpers/progress_helper';
import useSegment from '@/hooks/useSegment';

import { EntryList } from '../EntryList';

import { Table } from './Table';

const EVENT = 'Progress chart all entries viewed';
const NAV_EVENT = 'Progress chart date changed';

export function WeeklyTableData({ question }) {
  const { t } = useTranslation();
  const { sendTrackEvent } = useSegment();
  const { pathname } = useLocation();

  const [startOfWeek, setStartOfWeek] = useState(dayjs().startOf('isoWeek'));

  const eventData = {
    id: question.id,
    timeFrame: 'week',
    pathname,
    procedureId: question.procedureId,
    procedureName: question.surveyName,
    questionType: question.type,
    title: question.title,
  };

  useEffect(() => {
    sendTrackEvent(EVENT, eventData);
  }, []);

  const trackNavigation = (direction, date) => {
    sendTrackEvent(NAV_EVENT, {
      ...eventData,
      direction,
      startDate: dayjs(date).format('YYYY-MM-DD').toString(),
    });
  };

  const getPrevWeek = () => {
    const prevWeek = dayjs(startOfWeek).subtract(1, 'weeks');
    setStartOfWeek(prevWeek);
    trackNavigation('back', prevWeek);
  };

  const disableBack = useMemo(() => {
    const { patientEntries } = question;
    const firstEntryDate = _.first(patientEntries).date;
    return dayjs(firstEntryDate).isSameOrAfter(startOfWeek, 'isoWeek');
  }, [question, startOfWeek]);

  const getNextWeek = () => {
    const nextWeek = dayjs(startOfWeek).add(1, 'weeks');
    setStartOfWeek(nextWeek);
    trackNavigation('next', nextWeek);
  };

  const disableNext = useMemo(
    () => dayjs().isSameOrBefore(startOfWeek, 'isoWeek'),
    [startOfWeek]
  );

  const entriesForWeek = useMemo(() => {
    const { patientEntries } = question;
    const data = [];

    for (let i = 0; i <= 6; i += 1) {
      const dayOfWeek = dayjs(startOfWeek).add(i, 'days');
      const datum = {};
      // if there are multiple entries on the same day, get the last entry
      const entry = _.findLast(patientEntries, ({ date }) =>
        dayjs(date).isSame(dayOfWeek, 'day')
      );

      if (entry) {
        datum.date = entry.date;
        datum.dateLabel = dayjs(entry.date).format('ddd');
        datum.answer = entry.answer;
        datum.skipped = false;
      } else {
        datum.date = dayOfWeek;
        datum.dateLabel = dayjs(dayOfWeek).format('ddd');
        datum.skipped = true;
      }
      data.push(datum);
    }

    return data;
  }, [startOfWeek, question]);

  return (
    <>
      <h1 className="font-semibold text-lg">{question.chartTitle}</h1>
      <h3 className="text-sm my-4">{labelForWeek(startOfWeek)}</h3>
      <div className="flex flex-row">
        <Button
          color="primary"
          isDisabled={disableBack}
          variant="light"
          onPress={getPrevWeek}
        >
          <Icon icon="mdi:chevron-left" />
          {t('common.action.back')}
        </Button>

        <Button
          color="primary"
          isDisabled={disableNext}
          variant="light"
          onPress={getNextWeek}
        >
          {t('common.action.next')}
          <Icon icon="mdi:chevron-right" />
        </Button>
      </div>
      <Table question={question} entries={entriesForWeek} />
      <EntryList
        question={question}
        filteredEntries={entriesForWeek}
        mode="week"
      />
    </>
  );
}
